import * as React from "react";
import { cn } from "../../../helpers";
import styles from "./Tabs.module.scss";

export interface TabItem {
    /** Tab item label */
    label: string;
    /** Tab item key */
    key: string;
    /** Tab item children */
    children: React.ReactNode;
    /**  Tab item disabled prop */
    disabled?: boolean;
    /** Prop to hide tab item */
    hide?: boolean;
    /** Badge prop */
    hasBadge?: boolean;
    /** Item count value */
    count?: React.ReactNode;
    /** On tab item click */
    onClick?: () => void;
}

export interface TabsProps {
    /** Items in the tabs */
    items: TabItem[];
    /**  Tabs size */
    size?: "medium" | "small";
    /** Optional prop to set default active tab */
    defaultActiveKey?: string;
    /** Optional prop to style header */
    className?: string;
    /** showing content at the right side of the tabs in a particular view this props states which tabview to show content in */
    viewToShowRightContent?: string;
    /** Right content to show */
    rightContent?: React.ReactNode;
    /** Header Content before tab items */
    customHeader?: React.ReactNode;
}

export function Tabs({
    items,
    defaultActiveKey,
    size = "medium",
    className,
    viewToShowRightContent,
    rightContent,
    customHeader,
}: TabsProps) {
    const [tabInView, setTabInView] = React.useState(
        defaultActiveKey || items[0].key
    );

    const handleTabInViewInternal = (tabId: string) => {
        if (tabInView === tabId) return;

        setTabInView(tabId);
    };

    return (
        <>
            <div className={styles.tabWrapper}>
                <div role="tablist" className={cn(styles.tabList, className)}>
                    {items.map(
                        (item) =>
                            !item.hide && (
                                <button
                                    disabled={item.disabled}
                                    key={item.key}
                                    onClick={() => {
                                        handleTabInViewInternal(item.key);
                                        item.onClick?.();
                                    }}
                                    className={cn(
                                        styles.tabButton,
                                        tabInView === item.key &&
                                            styles.tabButtonActive,
                                        item.disabled && styles.tabButtonDisabled
                                    )}
                                    aria-label={item.key}
                                >
                                    {item.label}
                                    {item.count && (
                                        <span
                                            className={cn(
                                                styles.badge,
                                                tabInView === item.key &&
                                                    styles.badgeActive
                                            )}
                                        >
                                            {item.count}
                                        </span>
                                    )}
                                </button>
                            )
                    )}
                </div>
                {rightContent && tabInView === viewToShowRightContent && (
                    <div>{rightContent}</div>
                )}
            </div>

            {customHeader}
            <div>{items.find((item) => item.key === tabInView)?.children}</div>
        </>
    );
}
